import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import InternalBackLink from '../Internal/InternalBackLink';
import { useAuth } from '../Auth/AuthGate';
import {
  addAllowlist,
  isValidEmailFormat,
  listAllowlist,
  listUsers,
  normalizeEmail,
  revokeAccess,
  sendInvite,
  setUserRole,
} from '../../services/nativeAuth';
import { readTheme } from '../../utils/internalTheme';
import './InternalIndex.css';

/**
 * Admin user management — invites, allowlist, roles.
 * Blueprint: docs/INTERNAL_INDEX.md · docs/NATIVE_AUTH.md
 */

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function statusLabel(status) {
  if (status === 'disabled') return 'revoked';
  if (status === 'active') return 'active';
  return 'invited';
}

function UsersPanel() {
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const refresh = useCallback(async () => {
    const [allowRes, usersRes] = await Promise.all([listAllowlist(), listUsers()]);
    if (!allowRes.ok) {
      setError(allowRes.data.error || 'could not load allowlist');
      setEntries([]);
    } else {
      setEntries(Array.isArray(allowRes.data.entries) ? allowRes.data.entries : []);
    }
    if (!usersRes.ok) {
      setError((prev) => prev || usersRes.data.error || 'could not load users');
      setUsers([]);
    } else {
      setUsers(Array.isArray(usersRes.data.users) ? usersRes.data.users : []);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      const [allowRes, usersRes] = await Promise.all([listAllowlist(), listUsers()]);
      if (cancelled) return;
      if (!allowRes.ok) {
        setError(allowRes.data.error || 'could not load allowlist');
        setEntries([]);
      } else {
        setEntries(Array.isArray(allowRes.data.entries) ? allowRes.data.entries : []);
      }
      if (!usersRes.ok) {
        setError((prev) => prev || usersRes.data.error || 'could not load users');
        setUsers([]);
      } else {
        setUsers(Array.isArray(usersRes.data.users) ? usersRes.data.users : []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const clearBanners = () => {
    setError('');
    setSuccess('');
  };

  const handleAdd = async () => {
    if (busyKey) return;
    const email = normalizeEmail(emailInput);
    clearBanners();
    if (!isValidEmailFormat(email)) {
      setError('enter a valid email address');
      return;
    }
    setBusyKey('add');
    try {
      const result = await addAllowlist(email);
      if (!result.ok) {
        setError(result.data.error || 'could not allowlist');
        return;
      }
      setSuccess(result.data.message || `Allowlisted ${email}.`);
      setEmailInput('');
      await refresh();
    } catch {
      setError('could not allowlist');
    } finally {
      setBusyKey('');
    }
  };

  const handleInvite = async (email, { fromForm = false } = {}) => {
    if (busyKey) return;
    const target = normalizeEmail(email);
    clearBanners();
    if (!isValidEmailFormat(target)) {
      setError('enter a valid email address');
      return;
    }
    setBusyKey(fromForm ? 'invite-form' : `invite:${target}`);
    try {
      const result = await sendInvite(target);
      if (result.data.code === 'EMAIL_SEND_FAILED' || result.status === 502) {
        setError(
          result.data.error || 'Allowlisted, but invite email failed — try Resend.'
        );
        if (fromForm) setEmailInput(target);
        await refresh();
        return;
      }
      if (!result.ok) {
        setError(result.data.error || 'could not send invite');
        await refresh();
        return;
      }
      setSuccess(result.data.message || `Invite sent to ${target}.`);
      if (fromForm) setEmailInput('');
      await refresh();
    } catch {
      setError('could not send invite');
    } finally {
      setBusyKey('');
    }
  };

  const handleRevoke = async (email) => {
    if (busyKey) return;
    const target = normalizeEmail(email);
    const ok = window.confirm(
      `Roll back access for ${target}? They will be signed out.`
    );
    if (!ok) return;
    clearBanners();
    setBusyKey(`revoke:${target}`);
    try {
      const result = await revokeAccess(target);
      if (!result.ok) {
        setError(result.data.error || 'could not revoke');
        return;
      }
      setSuccess(result.data.message || `Access revoked for ${target}.`);
      setEntries((prev) => prev.filter((e) => e.email !== target));
      await refresh();
    } catch {
      setError('could not revoke');
    } finally {
      setBusyKey('');
    }
  };

  const handleRoleChange = async (email, role) => {
    if (busyKey) return;
    const target = normalizeEmail(email);
    clearBanners();
    setBusyKey(`role:${target}`);
    try {
      const result = await setUserRole(target, role);
      if (!result.ok) {
        setError(result.data.error || 'could not set role');
        return;
      }
      setSuccess(result.data.message || `Set ${target} to ${role}.`);
      await refresh();
    } catch {
      setError('could not set role');
    } finally {
      setBusyKey('');
    }
  };

  const formBusy = busyKey === 'add' || busyKey === 'invite-form';

  return (
    <section className="access-panel access-panel--page" aria-labelledby="users-invites-title">
      <div className="access-panel__form">
        <div className="access-panel__row">
          <span className="access-panel__prefix" aria-hidden="true">
            &gt;
          </span>
          <input
            className="access-panel__input"
            type="text"
            inputMode="email"
            autoComplete="off"
            placeholder="guest@example.com"
            value={emailInput}
            onChange={(e) => {
              setEmailInput(e.target.value);
              if (error) setError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleInvite(emailInput, { fromForm: true });
              }
            }}
            disabled={Boolean(busyKey)}
            aria-label="External email"
          />
        </div>
        <div className="access-panel__actions">
          <button
            type="button"
            className="access-panel__btn"
            disabled={formBusy}
            onClick={handleAdd}
          >
            {busyKey === 'add' ? '…' : 'Add'}
          </button>
          <button
            type="button"
            className="access-panel__btn access-panel__btn--accent"
            disabled={formBusy}
            onClick={() => handleInvite(emailInput, { fromForm: true })}
          >
            {busyKey === 'invite-form' ? '…' : 'Invite'}
          </button>
        </div>
      </div>

      {error ? (
        <p className="access-panel__banner access-panel__banner--err" role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="access-panel__banner access-panel__banner--ok" role="status">
          {success}
        </p>
      ) : null}

      <h2 className="access-panel__section" id="users-invites-title">
        Invites
      </h2>
      <div className="access-panel__table-wrap">
        {loading ? (
          <p className="access-panel__empty">loading…</p>
        ) : entries.length === 0 ? (
          <p className="access-panel__empty">no external allowlist entries</p>
        ) : (
          <table className="access-panel__table">
            <thead>
              <tr>
                <th scope="col">email</th>
                <th scope="col">status</th>
                <th scope="col">invited by</th>
                <th scope="col">last invite</th>
                <th scope="col"> </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((row) => {
                const rowBusy =
                  busyKey === `invite:${row.email}` ||
                  busyKey === `revoke:${row.email}`;
                return (
                  <tr key={row.email}>
                    <td className="access-panel__email">{row.email}</td>
                    <td>
                      <span
                        className={`access-panel__status access-panel__status--${statusLabel(row.status)}`}
                      >
                        {statusLabel(row.status)}
                      </span>
                    </td>
                    <td className="access-panel__muted">{row.invitedBy || '—'}</td>
                    <td className="access-panel__muted">
                      {formatWhen(row.lastInvitedAt || row.invitedAt)}
                    </td>
                    <td className="access-panel__row-actions">
                      <button
                        type="button"
                        className="access-panel__link"
                        disabled={rowBusy || Boolean(busyKey)}
                        onClick={() => handleInvite(row.email)}
                      >
                        {busyKey === `invite:${row.email}`
                          ? '…'
                          : row.status === 'invited'
                            ? 'Resend'
                            : 'Invite'}
                      </button>
                      <button
                        type="button"
                        className="access-panel__link access-panel__link--danger"
                        disabled={rowBusy || Boolean(busyKey)}
                        onClick={() => handleRevoke(row.email)}
                      >
                        {busyKey === `revoke:${row.email}` ? '…' : 'Revoke'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <h2 className="access-panel__section">Users &amp; roles</h2>
      <div className="access-panel__table-wrap">
        {loading ? (
          <p className="access-panel__empty">loading…</p>
        ) : users.length === 0 ? (
          <p className="access-panel__empty">no users</p>
        ) : (
          <table className="access-panel__table">
            <thead>
              <tr>
                <th scope="col">email</th>
                <th scope="col">role</th>
                <th scope="col">status</th>
                <th scope="col"> </th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const rowBusy = busyKey === `role:${u.email}`;
                const accountStatus = u.disabledAt
                  ? 'revoked'
                  : u.emailVerifiedAt
                    ? 'active'
                    : 'pending';
                return (
                  <tr key={u.id || u.email}>
                    <td className="access-panel__email">{u.email}</td>
                    <td>
                      <span
                        className={`access-panel__status access-panel__status--${u.role}`}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td className="access-panel__muted">{accountStatus}</td>
                    <td className="access-panel__row-actions">
                      {u.role === 'admin' ? (
                        <button
                          type="button"
                          className="access-panel__link"
                          disabled={rowBusy || Boolean(busyKey)}
                          onClick={() => handleRoleChange(u.email, 'member')}
                        >
                          {rowBusy ? '…' : 'Make member'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="access-panel__link"
                          disabled={
                            rowBusy || Boolean(busyKey) || Boolean(u.disabledAt)
                          }
                          onClick={() => handleRoleChange(u.email, 'admin')}
                        >
                          {rowBusy ? '…' : 'Make admin'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

export default function InternalUsers() {
  const { isAdmin } = useAuth();
  const [theme, setTheme] = useState(readTheme);

  useEffect(() => {
    const onTheme = (e) => {
      if (typeof e.detail === 'string') setTheme(e.detail);
      else setTheme(readTheme());
    };
    window.addEventListener('construx-internal-theme', onTheme);
    return () => window.removeEventListener('construx-internal-theme', onTheme);
  }, []);

  if (!isAdmin) {
    return <Navigate to="/internal" replace />;
  }

  return (
    <div
      className="internal-index"
      data-hub-theme={theme === 'light' ? undefined : theme}
    >
      <Helmet>
        <title>users · internal</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className="hub">
        <div className="hub-card">
          <div className="hub-h">
            <InternalBackLink />
          </div>
          <h1 className="hub-title">Users</h1>
          <p className="hub-sub">
            External invites, allowlist revoke, and roles. Admins only.
          </p>

          <UsersPanel />
        </div>
      </div>
    </div>
  );
}
